import React, { useState, useEffect } from "react";
import { Edit, X, Clock, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const timezones = [
  "Asia/Kolkata",
  "America/New_York",
  "Europe/London",
  "UTC",
  "Australia/Sydney",
];

const DEFAULT_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DEFAULT_START_TIME = "10:00";
const DEFAULT_END_TIME = "18:00";
const DEFAULT_TIMEZONE = "Asia/Kolkata";

const AiReplyScheduleField = ({
  aiReplyDays = [],
  aiReplyStartTime = "",
  aiReplyEndTime = "",
  aiTimezone = "UTC",
  onSave,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDays, setSelectedDays] = useState(
    aiReplyDays.length > 0 ? aiReplyDays : DEFAULT_DAYS
  );
  const [startTime, setStartTime] = useState(aiReplyStartTime || DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(aiReplyEndTime || DEFAULT_END_TIME);
  const [timezone, setTimezone] = useState(aiTimezone || DEFAULT_TIMEZONE);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setSelectedDays(aiReplyDays.length > 0 ? aiReplyDays : DEFAULT_DAYS);
      setStartTime(aiReplyStartTime || DEFAULT_START_TIME);
      setEndTime(aiReplyEndTime || DEFAULT_END_TIME);
      setTimezone(aiTimezone || DEFAULT_TIMEZONE);
    }
  }, [aiReplyDays, aiReplyStartTime, aiReplyEndTime, aiTimezone, isEditing]);

  const toggleDay = (day) =>
    selectedDays.includes(day)
      ? setSelectedDays(selectedDays.filter((d) => d !== day))
      : setSelectedDays([...selectedDays, day]);

      const handleCancel = () => {
        setSelectedDays(aiReplyDays.length > 0 ? aiReplyDays : DEFAULT_DAYS);
        setStartTime(aiReplyStartTime || DEFAULT_START_TIME);
        setEndTime(aiReplyEndTime || DEFAULT_END_TIME);
        setTimezone(aiTimezone || DEFAULT_TIMEZONE);
        setIsEditing(false);
      };
    

  const handleSave = async () => {
    if (!startTime || !endTime) {
      alert("Please set both start and end times.");
      return;
    }
    if (startTime >= endTime) {
      alert("End time must be after start time.");
      return;
    }
    setIsSaving(true);
    try {
      await onSave({
        ai_reply_days: selectedDays,
        ai_reply_start_time: startTime,
        ai_reply_end_time: endTime,
        ai_timezone: timezone, // <-- pass new field
      });
      setJustSaved(true);
      setIsEditing(false);
      setTimeout(() => setJustSaved(false), 3000);
    } catch {
      alert("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Display mode
  if (!isEditing) {
    return (
      <div className="space-y-2 my-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center">
          <span className="flex items-center space-x-2 font-medium text-gray-900">
            <Clock className="text-[#01CC54]" size={18} />
            <div className="flex flex-col leading-tight">
              <span>AI Reply Schedule</span>
              <small className="text-gray-500 text-xs">
                Set days and time range for AI chatbot to reply automatically
              </small>
            </div>
          </span>
          {!disabled && (
            <motion.button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Edit size={16} />
            </motion.button>
          )}
        </div>
        <div className="text-sm text-gray-600">
          <strong>Days:</strong>{" "}
          {selectedDays.length > 0
            ? selectedDays
                .map((d) => d[0].toUpperCase() + d.slice(1))
                .join(", ")
            : "Not set"}
        </div>
        <div className="text-sm text-gray-600">
          <strong>Time:</strong>{" "}
          {startTime && endTime ? `${startTime} - ${endTime}` : "Not set"}
        </div>
        <div className="text-sm text-gray-600">
          <strong>Time Zone:</strong> {aiTimezone ? aiTimezone : "Not set"}
        </div>
        <AnimatePresence>
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.25 }}
              className="flex items-center text-[#01CC54] text-sm space-x-1 mt-1"
            >
              <Check size={14} />
              <span>Saved successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex justify-between items-center">
        <span className="flex items-center space-x-2 font-medium text-gray-900">
          <Clock className="text-[#01CC54]" size={18} />
          <div className="flex flex-col leading-tight">
            <span>Edit AI Reply Schedule</span>
            <small className="text-gray-500 text-xs">
              Set days and time range for AI chatbot to reply automatically
            </small>
          </div>
        </span>
        <motion.button
          onClick={handleCancel}
          className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X size={16} />
        </motion.button>
      </div>

      {/* Days */}
      <div className="flex flex-wrap gap-8">
        {weekDays.map((day) => (
          <label key={day} className="inline-flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedDays.includes(day)}
              onChange={() => toggleDay(day)}
              className="rounded border-gray-200 bg-white text-[#048AAD] focus:ring-2 focus:ring-[#01CC54] w-5 h-5"
            />
            <span className="capitalize text-gray-900 font-medium">{day}</span>
          </label>
        ))}
      </div>

      {/* Time Inputs */}
      <div className="flex flex-col gap-2 px-8">
        <div className="flex gap-8">
          <div className="flex flex-col">
            <span className="flex items-center mb-1 text-gray-700">
              <Clock size={18} className="text-[#01CC54] mr-1" /> Start Time
            </span>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="rounded border border-gray-200 p-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#01CC54] focus:border-[#01CC54]"
            />
          </div>
          <div className="flex flex-col">
            <span className="flex items-center mb-1 text-gray-700">
              <Clock size={18} className="text-[#01CC54] mr-1" /> End Time
            </span>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="rounded border border-gray-200 p-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#01CC54] focus:border-[#01CC54]"
            />
          </div>
          {/* Timezone Selector */}
          <div className="flex flex-col">
            <span className="flex items-center mb-1 text-gray-700">
              <Clock size={18} className="text-[#01CC54] mr-1" /> Timezone
            </span>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="rounded border border-gray-200 p-2 text-gray-900 bg-white focus:ring-2 focus:ring-[#01CC54] focus:border-[#01CC54]"
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-2">
        <motion.button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-4 py-1 rounded text-sm font-medium text-white ${
            isSaving
              ? "bg-gray-100 cursor-not-allowed text-gray-400"
              : "bg-[#048AAD] hover:bg-[#048AAD]/90"
          }`}
        >
          {isSaving ? (
            "Saving..."
          ) : (
            <>
              <Check size={14} className="inline mr-1" /> Save
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default AiReplyScheduleField;
